import "./editable-field.less"

import { useMessages } from "play/language/language"
import { asPromise, boolOr, classNames, hasText } from "utils/helpers"
import TextInputGroup, { TextInputProps } from "./text-input-group"
import { useEffect, useState } from "react"

const AcceptChangesImageUrl = "/img/correct.png"
const CancelChangesImageUrl = "/img/incorrect.png"
const EditImageUrl = "/img/icon_pencil_small.png"

export interface EditableFieldProps extends TextInputProps {
  required?: boolean

  onClearValidation(): void
}

export default function EditableField(props: EditableFieldProps) {
  const [state, setState] = useState(() => ({
    editMode: false,
    editedValue: "",
  }))
  const msg = useMessages()

  useEffect(() => {
    if (props.validationError) {
      handleEnterEditMode(false)
    }
  }, [props.validationError])

  function handleInterimValueChanged(newValue: string) {
    setState((prevState) => ({
      ...prevState,
      editedValue: newValue,
    }))
  }

  function exitEditMode() {
    setState((prevState) => ({
      ...prevState,
      editMode: false,
    }))
  }

  function handleAcceptChanges() {
    const isRequired = boolOr(props.required, true)
    if (isRequired && !hasText(state.editedValue)) {
      return
    }

    const r = props.onChange(state.editedValue)
    const promise = asPromise(r)

    if (promise) {
      promise.then(exitEditMode).catch(exitEditMode)
    } else {
      exitEditMode()
    }
  }

  function handleCancelChanges() {
    props.onClearValidation()

    setState((prevState) => ({
      ...prevState,
      editedValue: props.value,
      editMode: false,
    }))
  }

  function handleEnterEditMode(clear: boolean) {
    if (clear) {
      props.onClearValidation()
      setState((prevState) => ({
        ...prevState,
        editedValue: props.value,
        editMode: true,
      }))
    } else {
      setState((prevState) => ({
        ...prevState,
        editMode: true,
      }))
    }
  }

  return (
    <div className={classNames("editable-field", props.className)}>
      {state.editMode ? (
        <>
          <TextInputGroup
            id={props.id}
            value={state.editedValue}
            onChange={handleInterimValueChanged}
            validationError={props.validationError}
            disabled={props.disabled}
            label={props.label}
            postfix={props.postfix}
            prefix={props.prefix}
            placeholder={props.placeholder}
          />
          <button
            key="accept-changes"
            className="rounded btn btn-primary"
            onClick={handleAcceptChanges}
            disabled={props.disabled}
          >
            <img src={AcceptChangesImageUrl} alt={msg.acceptChanges} />
          </button>
          <button
            key="cancel-changes"
            className="rounded btn btn-secondary"
            onClick={handleCancelChanges}
            disabled={props.disabled}
          >
            <img src={CancelChangesImageUrl} alt={msg.cancelChanges} />
          </button>
        </>
      ) : (
        <>
          <div className="space">
            <div className="space-inner"></div>
          </div>
          {props.value}
          <button
            key="enter-edit-mode"
            className="edit btn btn-primary"
            onClick={() => handleEnterEditMode(true)}
            disabled={props.disabled}
          >
            <img src={EditImageUrl} alt={msg.edit} />
          </button>
        </>
      )}
    </div>
  )
}
